import { useContext, useEffect, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import {
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaCommentAlt,
  FaFilter,
  FaMapMarkerAlt,
  FaSearch,
  FaSeedling,
  FaStar,
  FaStore,
} from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../../../assets/css/customer/shop/Shop.css";
import Footer from "../../../components/general/footer/Footer";
import MyNavbar from "../../../components/general/navbar/MyNavbar";
import PageHero from "../../../components/general/pageHero/PageHero";
import Pagination from "../../../components/general/pagination/Pagination";
import { ShopsPageSkeleton } from "../../../components/general/skeleton/SkeletonBlock";
import CTA from "../../../subscreens/customer/business/CTA";
import Swal from "sweetalert2";
import axios from "axios";
import MyContext from "../../../context/MyContext";
import { PLACEHOLDER_SHOP } from "../../../utils/placeholder";

const Shops = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    baseUrl,
    baseUrlForFiles,
    setSelectedShop,
    loggedInCustomer,
    authToken,
  } = useContext(MyContext);

  const urlRestored = useRef(false);
  const [shopsData, setShopsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRating, setSelectedRating] = useState("all");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  //  viewAllShops — city-lock hata di gayi hai (Intercity Bulk Delivery
  //  spec), sab cities ke shops aate hain ab ──
  const viewAllShops = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/customer/shops/viewAllShops`)
      .then((res) => {
        setLoading(false);
        if (res.data.hasOwnProperty("error")) {
          Swal.fire({ icon: "error", title: "Error", text: res.data.error });
        } else {
          setShopsData(res.data.shops);
        }
      })
      .catch((err) => {
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load Shops",
        });
      });
  };

  //  createConversation ───────────────────────────────────
  const createConversation = (shop) => {
    if (!loggedInCustomer?.id || !authToken) {
      navigate("/customer/login");
      return;
    }

    const formData = new FormData();
    formData.append("kmseller_id", shop.id);
    formData.append("type", "general");
    formData.append("lastMessage", "");

    axios
      .post(`${baseUrl}/customer/conversations/createConversation`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.success == true) {
          navigate("/customer/messages");
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.message,
          });
        }
      })
      .catch((err) => {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to create conversation",
        });
      });
  };

  // Effects ───────────────────────────────────────────────────
  useEffect(() => {
    viewAllShops();
  }, []);

  // ── Filtering Logic ───────────────────────────────────────────
  const filteredShops = shopsData.filter((shop) => {
    const matchesSearch =
      shop.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      shop.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesRating =
      selectedRating === "all" ||
      (selectedRating === "4+" && shop.rating >= 4) ||
      (selectedRating === "4.5+" && shop.rating >= 4.5);

    return matchesSearch && matchesRating;
  });

  // ── FIXED: koi filter active hai ya nahi — Clear Filters button
  //     sirf tab dikhna chahiye jab search/rating filter lagaya gaya
  //     ho, na ke tab jab shops list hi shuru se khali ho ──
  const isFilterActive = searchQuery.trim() !== "" || selectedRating !== "all";

  // ── Pagination Logic ──────────────────────────────────────────
  const indexOfLastShop = currentPage * itemsPerPage;
  const indexOfFirstShop = indexOfLastShop - itemsPerPage;
  const currentShops = filteredShops.slice(indexOfFirstShop, indexOfLastShop);

  // ── Loading ───────────────────────────────────────────────────
  if (loading)
    return (
      <div className="shops-page-container">
        <MyNavbar />
        <ShopsPageSkeleton />
        <Footer />
      </div>
    );

  return (
    <>
      <div className="shops-page-container">
        <MyNavbar />
        <PageHero title="Our Shops" breadcrumb="Shops" overlayType="mixed" />

        <Container className="shops-main-content">
          <Row>
            {/* ── SIDEBAR ── */}
            <Col lg={3} md={4} className="sidebar-col">
              <div className="shops-sidebar">
                {/* Search */}
                <div className="sidebar-widget">
                  <div className="widget-header">
                    <FaSearch className="widget-icon" />
                    <h3 className="sidebar-title">Search</h3>
                  </div>
                  <div className="sidebar-search">
                    <button className="sidebar-search-icon">
                      <FaSearch />
                    </button>
                    <input
                      type="text"
                      placeholder="Search shops..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="sidebar-search-input"
                    />
                  </div>
                </div>
              </div>
            </Col>

            {/* ── SHOPS LIST ── */}
            <Col lg={9} md={8} className="content-col">
              <div className="shops-results-header">
                <p className="shops-results-count">
                  Showing
                  {filteredShops.length === 0 ? 0 : indexOfFirstShop + 1} to
                  {Math.min(indexOfLastShop, filteredShops.length)} of
                  {filteredShops.length} shops
                </p>
              </div>

              <Row className="g-4">
                {currentShops.length > 0 ? (
                  currentShops.map((shop, index) => (
                    <Col key={shop.id} xs={12}>
                      <div
                        className="shop-card-item"
                        onClick={() => {
                          setSelectedShop(shop);
                          navigate(`/shop-details`);
                        }}
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        {/* Image */}
                        <div className="shop-card-image-section">
                          <img
                            src={
                              shop.kmbusinessdetails?.logo
                                ? `${baseUrlForFiles}/${shop.kmbusinessdetails.logo}`
                                : PLACEHOLDER_SHOP
                            }
                            alt={shop.shop_name || shop.name}
                            className="shop-card-image"
                            onError={(e) => {
                              e.currentTarget.onerror = null; // loop band
                              e.currentTarget.src = PLACEHOLDER_SHOP;
                            }}
                          />
                        </div>

                        {/* Details */}
                        <div className="shop-card-details-section">
                          <div className="shop-card-header">
                            <h3 className="shop-card-name">
                              {shop.shop_name || shop.name}
                            </h3>
                            {shop.verified && (
                              <div className="shop-card-verified">
                                <FaCheckCircle />
                              </div>
                            )}
                          </div>

                          <div className="shop-card-rating">
                            <div className="shop-rating-stars">
                              {[...Array(5)].map((_, i) => (
                                <FaStar
                                  key={i}
                                  className={
                                    i < Math.floor(shop.rating || 0)
                                      ? "star-filled"
                                      : "star-empty"
                                  }
                                />
                              ))}
                            </div>
                            {shop.rating && (
                              <span className="shop-rating-value">
                                ({Number(shop.rating).toFixed(1)})
                              </span>
                            )}
                          </div>

                          <p className="shop-card-description">
                            {shop.description}
                          </p>

                          <div className="shop-card-footer">
                            <div className="shop-location-info">
                              <FaMapMarkerAlt className="shop-location-icon" />
                              <span>{shop.location}</span>
                            </div>

                            {/* Message Button */}
                            <button
                              className="shop-message-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                createConversation(shop);
                              }}
                              title="Message Shop"
                            >
                              <FaCommentAlt />
                            </button>
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))
                ) : (
                  <Col xs={12}>
                    <div className="shops-no-results">
                      <FaSeedling className="no-results-icon" />
                      <p>
                        {isFilterActive
                          ? "No shops found matching your criteria"
                          : "No shops found"}
                      </p>
                      {isFilterActive && (
                        <button
                          className="no-results-btn"
                          onClick={() => {
                            setSearchQuery("");
                            setSelectedRating("all");
                            setCurrentPage(1);
                            setSearchParams({});
                          }}
                        >
                          Clear Filters
                        </button>
                      )}
                    </div>
                  </Col>
                )}
              </Row>

              {filteredShops.length > 0 && (
                <div className="shops-pagination-wrapper">
                  <Pagination
                    totalItems={filteredShops.length}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={(page) => {
                      setCurrentPage(page);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    onItemsPerPageChange={(n) => {
                      setItemsPerPage(n);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              )}
            </Col>
          </Row>
        </Container>

        <CTA />
        <Footer />
      </div>
    </>
  );
};

export default Shops;
